import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AspirantSignupResponse } from '@udbodhanam/shared';
import { formatNumber } from '../../lib/formatters';
import ThemeToggle from '../UI/ThemeToggle';
import LanguageSelector from '../UI/LanguageSelector';

interface Props {
  checkoutData: AspirantSignupResponse;
  onProceed: () => void;
}

export default function PlanConfirmation({ checkoutData, onProceed }: Props) {
  const { t } = useTranslation('auth');

  // Computed inside the component body (not module scope) — module-level
  // constants would evaluate before i18n loads and capture the fallback language.
  const planNameMap = useMemo<Record<string, string>>(
    () => ({
      YEARLY: t('payment.confirmation.planName.YEARLY'),
      HALF_YEARLY: t('payment.confirmation.planName.HALF_YEARLY', { defaultValue: '' }),
      MONTHLY: t('payment.confirmation.planName.MONTHLY', { defaultValue: '' }),
    }),
    [t],
  );

  const planDescriptionMap = useMemo<Record<string, string>>(
    () => ({
      YEARLY: t('payment.confirmation.description.YEARLY'),
      HALF_YEARLY: t('payment.confirmation.description.HALF_YEARLY', { defaultValue: '' }),
      MONTHLY: t('payment.confirmation.description.MONTHLY', { defaultValue: '' }),
    }),
    [t],
  );

  const planName = planNameMap[checkoutData.plan_type] || checkoutData.plan_type;
  const planDescription = planDescriptionMap[checkoutData.plan_type] || '';
  const priceDisplay = `₹${formatNumber(checkoutData.amount / 100)}`;

  return (
    // Mirrors PaymentRequired.tsx / ForcePasswordChange.tsx's two-panel branded
    // layout — this is a real destination screen the aspirant is meant to read,
    // not a transient loading state (unlike RazorpayCheckout's minimal shell,
    // which was the wrong visual reference for this component originally).
    <main className="flex min-h-screen">
      {/* LEFT — orange hero panel */}
      <div className="hidden md:flex flex-1 bg-primary-500 text-white relative overflow-hidden flex-col justify-center px-16 py-16">
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ top: -140, right: -140, width: 460, height: 460, background: 'rgba(255,255,255,0.10)' }}
        />
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ top: -50, right: -50, width: 300, height: 300, background: 'rgba(255,255,255,0.10)' }}
        />
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ bottom: -180, left: -120, width: 380, height: 380, background: 'rgba(255,255,255,0.08)' }}
        />
        <div className="relative">
          <h1 className="font-display text-[56px] leading-[64px] font-semibold text-white tracking-[-0.01em]">
            Udbodhanam
          </h1>
          <p className="font-display italic text-white/90 text-2xl mt-3 font-medium">
            &ldquo;Awakening through knowledge&rdquo;
          </p>
        </div>
      </div>

      {/* RIGHT — content panel */}
      <div className="w-full md:flex-1 bg-surface flex flex-col relative">
        <div className="absolute top-5 right-5 md:top-7 md:right-8 flex items-center gap-2 z-10">
          <ThemeToggle />
          <LanguageSelector />
        </div>

        <div className="flex-1 flex flex-col md:justify-center px-6 pb-10 pt-16 md:px-[72px] md:py-[60px]">
          <div className="max-w-[440px] w-full mx-auto">
            <h2 className="text-[36px] leading-[44px] font-bold text-earth-800 tracking-[-0.015em]">
              {t('payment.confirmation.title')}
            </h2>

            <div className="mt-6 rounded-lg border border-border bg-surface-2 px-5 py-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-muted">{t('payment.confirmation.planLabel')}</span>
                <span className="text-sm font-medium text-earth-800">{planName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-muted">{t('payment.confirmation.durationLabel')}</span>
                <span className="text-sm font-medium text-earth-800">
                  {t('payment.confirmation.durationDays', { count: checkoutData.duration_days })}
                </span>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <span className="text-sm text-text-muted">{t('payment.confirmation.priceLabel')}</span>
                <span className="text-4xl font-bold tabular-nums tracking-tight text-earth-800">
                  {priceDisplay}
                </span>
              </div>
            </div>

            {planDescription && (
              <p className="text-earth-600 mt-4 text-sm leading-relaxed">{planDescription}</p>
            )}

            <button
              type="button"
              onClick={onProceed}
              className="mt-6 w-full bg-primary-500 text-white font-semibold rounded-lg py-2.5 text-sm hover:bg-primary-600 active:bg-primary-700"
            >
              {t('payment.confirmation.proceedButton')}
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
